import {
  useContext,
  ref,
  readonly,
  InjectionKey,
  inject,
  provide,
  computed,
  useRoute,
  watch,
} from '@nuxtjs/composition-api'
import { clone, equals, omit, props } from 'ramda'
import findKey from 'lodash.findkey'
import { useProductStockRefresh } from '../product/useProduct'
import { useLoading } from '../ui/useLoading'
import { useRouteSetting } from '../useRouteSetting'
import { getWebNodeNamesMap, useWebNode } from '../useWebNode'
import { useContent } from '../useContent'
import {
  Product,
  ProductListOptions,
  ProductSearchRequest,
  ProductFacet,
  ProductCondition,
  PriceRange,
  Range,
  FilterFields,
  ProductListMode,
  ListType,
} from '~/types/product'
import { Action, combineQuery, QueryAction } from '~/lib/utilities'

export const DEFAULT_LIMIT = 24
const FACET_KEYS_SET = new Set([
  'BrandTitle',
  'WebNodeIds',
  'ExternalLine',
  'ExternalFragFamily',
  'ExternalContent',
  'ExternalProductSkinType',
  'ExternalCoverage',
  'ExternalFinish',
  'ExternalSunProtection',
  'ExternalBenefits',
  'ExternalHairConcern',
  'GrossPrice',
])

export const useProductList = () => {
  const { app, i18n } = useContext()
  const loading = useLoading()
  const { combineCurrentQuery, setRouteQuery } = useRouteSetting()
  const route = useRoute()
  const { webNodeNamesMap } = useWebNode()
  const { article } = useContent()

  const products = ref<Product[]>([])
  const limit = ref(DEFAULT_LIMIT)
  const offset = ref(0)
  const total = ref(0)
  const activeSort = ref<string>('')
  const options = ref<ProductListOptions | null>(null)
  const serverOptions = ref<ProductListOptions | null>(null)
  const filters = ref<ProductFacet[]>([])
  const activeFilters = ref<ProductCondition[]>([])
  const priceRange = ref<PriceRange | null>(null)
  const currentPriceRange = ref<Range | null>(null)
  const hasResult = ref(true)

  const { productLoadedFromServer, onProductsLoaded, refreshStocks } =
    useProductStockRefresh(products)

  const ensureServerOptions = async () => {
    if (!serverOptions.value) {
      serverOptions.value = await app.$api.productMeili.getListOptions()
    }

    return serverOptions.value
  }

  const ensureOptions = async (
    buildOptions: () => Promise<ProductListOptions | null> | ProductListOptions
  ) => {
    const optionsPartials = await Promise.all([
      ensureServerOptions(),
      buildOptions(),
    ])
    options.value = Object.assign({}, optionsPartials[0], optionsPartials[1])

    if (options.value?.sortOptions?.length) {
      options.value.sortOptions.forEach((item) => {
        if (item.value === 'CreationDate:desc') activeSort.value = item.value
      })

      if (!activeSort.value) {
        activeSort.value = options.value.sortOptions[0].value
      }
    }

    return options.value
  }

  const limitOptionsSet = computed(
    () =>
      new Set(options.value?.limitOptions?.map((option) => option.value) ?? [])
  )

  const buildRequest = (
    request: ProductSearchRequest
  ): ProductSearchRequest => {
    const filters = new Map<string, string[]>()

    // Build from prefilter
    const prefilters = options.value?.prefilters
    if (prefilters?.length) {
      prefilters.forEach((prefilter) => {
        switch (prefilter.field) {
          case FilterFields.WebNodeId:
            request.webNodeId = prefilter.value as number
            break
          case FilterFields.Query:
            request.query = prefilter.value as string
            break
          default:
            filters.set(prefilter.field, [prefilter.value as string])
        }
      })
    }

    activeFilters.value.forEach((condition) => {
      const values = filters.get(condition.field)
      if (values) {
        values.push(condition.value as string)
      } else {
        filters.set(condition.field, [condition.value as string])
      }
    })

    request.filters = filters

    if (activeSort.value) {
      request.sortBy = activeSort.value
    }

    request.limit = request.limit || limit.value

    if (currentPriceRange.value) {
      request.priceRange = currentPriceRange.value
    }

    return request
  }

  const loadProducts = (readQuery: boolean = false) => {
    return loading.scope(async () => {
      if (readQuery) {
        setValuesFromQuery()
      }
      const request = buildRequest({
        offset: offset.value,
      })

      const response = await app.$api.productMeili.search(request)
      if (!response) return

      products.value = response.products
      total.value = response.total
      hasResult.value = !!products.value.length
      onProductsLoaded()

      let allowedFilters = response.facets

      if (options.value?.brandMode === ProductListMode.Navigation) {
        allowedFilters = allowedFilters.filter(
          (facet) => facet.field !== FilterFields.BrandTitle
        )
      }

      // Recover filters selection status
      const conditionsMap = new Map<string, Map<string, ProductCondition>>()
      activeFilters.value.forEach((condition) => {
        let groupMap = conditionsMap.get(condition.field)
        if (!groupMap) {
          groupMap = new Map()
          conditionsMap.set(condition.field, groupMap)
        }
        groupMap.set(condition.value as string, condition)
      })

      if (conditionsMap.size) {
        // remainingConditionsLength/remainingValuesLength is to optimize the speed of traversing an array
        let remainingConditionsLength = conditionsMap.size
        // usedFieldsSet to record the used fields
        const usedFieldsSet = new Set<string>()
        allowedFilters.some((facet) => {
          const valuesMap = conditionsMap.get(facet.field)
          if (valuesMap?.size) {
            remainingConditionsLength--
            usedFieldsSet.add(facet.field)
            let remainingValuesLength = valuesMap.size
            // usedValuesSet to record the used values
            const usedValuesSet = new Set<string>()
            facet.values.some((val) => {
              if (valuesMap.has(val.value)) {
                val.checked = true
                usedValuesSet.add(val.value)
                remainingValuesLength--
              }
              return remainingValuesLength === 0
            })
            // "remainingValuesLength/remainingConditionsLength > 0" means that some activeFilters are missing in the allowedFilters, need to add manually
            if (remainingValuesLength > 0) {
              valuesMap.forEach((val, key) => {
                if (!usedValuesSet.has(key)) {
                  facet.values.push({
                    label: val.label ?? '',
                    value: key,
                    count: 0,
                    checked: true,
                  })
                }
              })
            }
          }
          return remainingConditionsLength === 0
        })
        if (remainingConditionsLength > 0) {
          conditionsMap.forEach((val, key) => {
            if (!usedFieldsSet.has(key)) {
              allowedFilters.push({
                field: key,
                label: facetLabels.value?.[key] ?? '',
                values: Array.from(val.values()).map((v) => ({
                  label: v.label ?? '',
                  value: v.value as string,
                  count: 0,
                  checked: true,
                })),
              })
            }
          })
        }
      }

      filters.value = allowedFilters
      priceRange.value = response.priceRange ?? null
    })
  }

  const reloadProducts = async (shouldResetOffset = true) => {
    if (shouldResetOffset) {
      resetOffset()
    }
    await loadProducts()
  }

  const limitProducts = async (val: number) => {
    limit.value = val
    setRouteQuery(
      combineCurrentQuery([
        {
          key: FilterFields.Limit,
          values: val.toString(),
          action: val === DEFAULT_LIMIT ? Action.Empty : Action.Replace,
        },
        pageQueryAction.value,
      ])
    )
    await reloadProducts()
  }

  const offsetProducts = async (val: number) => {
    offset.value = val
    const currentPage = val / limit.value + 1
    setRouteQuery(
      combineCurrentQuery({
        key: FilterFields.Page,
        values: currentPage.toString(),
        action: currentPage > 1 ? Action.Replace : Action.Empty,
      })
    )
    await reloadProducts(false)
  }

  const resetOffset = () => {
    if (offset.value === 0) return
    offset.value = 0
  }

  const resetCurrentPriceRange = () => {
    if (currentPriceRange.value === null) return
    currentPriceRange.value = null
  }

  /* --- sort products --- */
  const sortProducts = async (val: string) => {
    activeSort.value = val
    setRouteQuery(combineCurrentQuery(pageQueryAction.value))
    await reloadProducts()
  }

  /* --- operate active filters  --- */
  const addActiveFilters = (
    condition: ProductCondition,
    replace: boolean = false
  ) => {
    let modified = false
    const conditionExisted = activeFilters.value.some((item) => {
      if (item.field === condition.field) {
        if (replace) {
          if (equals(item, condition)) return true
          Object.assign(item, condition)
          modified = true
        } else if ((item.value as string) === (condition.value as string)) {
          return true
        }
      }
      return false
    })

    if (modified) return true
    if (conditionExisted) return false

    activeFilters.value.push(condition)

    return true
  }

  const removeActiveFilters = (condition: ProductCondition) => {
    const index = activeFilters.value.findIndex(
      (item) =>
        item.field === condition.field &&
        (item.value as string) === (condition.value as string)
    )
    if (index < 0) return false

    activeFilters.value.splice(index, 1)
    return true
  }

  const clearActiveFilters = () => {
    if (!activeFilters.value.length) return
    activeFilters.value = []
  }

  const facetLabels = computed(() => {
    const facetLabels = clone(options.value?.facetLabels)
    if (!facetLabels) return null

    for (const key in facetLabels) {
      if (FACET_KEYS_SET.has(key)) {
        facetLabels[key] = i18n.t(`product.facet.${key}`) as string
      }
    }
    return facetLabels
  })

  const availableFiledsSet = computed(() => {
    let availableFileds: string[] = []
    if (facetLabels.value) {
      const obj = omit(
        [FilterFields.BrandUrl, FilterFields.Price],
        facetLabels.value
      )
      availableFileds = props(Object.keys(obj), obj)
    }

    return new Set(availableFileds)
  })

  const setPriceRangeFromQuery = () => {
    const query = route.value.query

    if (facetLabels.value?.[FilterFields.Price]) {
      const prices =
        (query[facetLabels.value[FilterFields.Price]] as string)
          ?.split('_')
          ?.map((price) => Number(price)) ?? []
      if (prices.length) {
        const minPrice = prices[0]
        const maxPrice = prices[1]
        if (!isNaN(minPrice) && !isNaN(maxPrice)) {
          currentPriceRange.value = {
            min: Number(minPrice.toFixed(2)),
            max: Number(maxPrice.toFixed(2)),
          }
          return
        }
      }
    }

    currentPriceRange.value = null
  }

  const setValuesFromQuery = () => {
    const query = route.value.query
    // set price filter
    setPriceRangeFromQuery()

    // set limit, should set limit first and then offset
    const limitFromQuery = Number(query[FilterFields.Limit])
    limit.value =
      limitFromQuery && limitOptionsSet.value.has(limitFromQuery)
        ? limitFromQuery
        : DEFAULT_LIMIT

    // set offset
    const currentPage = Number(query[FilterFields.Page])
    offset.value = currentPage ? (currentPage - 1) * limit.value : 0

    const remainingQuery = omit(
      [
        FilterFields.Page,
        FilterFields.Limit,
        ...(facetLabels.value?.[FilterFields.Price]
          ? [facetLabels.value[FilterFields.Price]]
          : []),
      ],
      query
    )
    const tempActiveFiltersMap = new Map<string, ProductCondition>()

    for (const key in remainingQuery) {
      const values: string[] = []
      const queryValue = remainingQuery[key] as string[] | string

      if (Array.isArray(queryValue)) {
        values.push(...queryValue)
      } else {
        values.push(queryValue)
      }

      if (availableFiledsSet.value.has(key)) {
        values.forEach((val) => {
          const field = findKey(facetLabels.value, (o: string) => o === key)
          const fieldValKey = field + val

          if (tempActiveFiltersMap.has(fieldValKey)) return

          tempActiveFiltersMap.set(fieldValKey, {
            label: val,
            field,
            value: val,
          })
        })
      }
    }

    activeFilters.value = Array.from(tempActiveFiltersMap.values())
  }

  /* --- current page query removed action --- */
  const pageQueryAction = computed(() => {
    return offset.value === 0
      ? ({} as QueryAction)
      : {
          key: FilterFields.Page,
          action: Action.Empty,
        }
  })

  /* --- current category namesMap --- */
  const categoryNamesMap = computed(() => {
    const category = options.value?.category
    if (!category) return null
    return getWebNodeNamesMap(category)
  })

  const getCategoryName = (id: number) => {
    return categoryNamesMap.value?.get(id) || webNodeNamesMap.value?.get(id)
  }

  const allowedCategoriesFromArticleCustomField1 = computed(() => {
    if (!article.value?.customField1) return null
    const ids = article.value.customField1
      .split('|')
      .map((item) => Number(item))
    return new Set(ids)
  })

  /* --- allowed filters, convert webNodeId to webNodeName --- */
  const allowedFilters = computed(() => {
    filters.value.some((item, index) => {
      if (item.field === FilterFields.WebNodeIds) {
        item.values = item.values.filter((val) => {
          const id = Number(val.value)
          const label = getCategoryName(id)
          const isHited =
            options.value?.listType === ListType.Brand &&
            allowedCategoriesFromArticleCustomField1.value
              ? allowedCategoriesFromArticleCustomField1.value.has(id)
              : true
          if (!label || !isHited) return false

          val.label = label
          return true
        })
        if (!item.values.length) {
          filters.value.splice(index, 1)
        }
        return true
      }

      return false
    })
    return filters.value
  })

  /* --- active filter groups --- */
  const activeFilterGroups = computed(() => {
    const groups = activeFilters.value.map((activeFilter) => {
      if (activeFilter.field === FilterFields.WebNodeIds) {
        const categoryName = getCategoryName(Number(activeFilter.value))
        if (categoryName) {
          activeFilter.label = categoryName
        }
      }
      return activeFilter
    })

    if (
      currentPriceRange.value !== null &&
      facetLabels.value?.[FilterFields.Price]
    ) {
      const val = `${facetLabels.value[FilterFields.Price]} ${app.i18n.n(
        currentPriceRange.value.min,
        'currency'
      )} - ${app.i18n.n(currentPriceRange.value.max, 'currency')}`

      groups.push({
        label: val,
        field: FilterFields.Price,
        value: val,
      })
    }

    return groups
  })

  const removeActiveFilterGroup = async (condition: ProductCondition) => {
    if (condition.field === FilterFields.Price) {
      resetCurrentPriceRange()

      if (facetLabels.value?.[FilterFields.Price]) {
        const queries = [
          {
            key: facetLabels.value[FilterFields.Price],
            action: Action.Empty,
          },
        ]
        setRouteQuery(combineCurrentQuery([...queries, pageQueryAction.value]))
      }

      await reloadProducts()
    } else {
      await unselectFilter(condition)
    }
  }

  /* --- filter products  --- */
  const selectFilter = async (condition: ProductCondition) => {
    addActiveFilters(condition)

    const key = facetLabels.value?.[condition.field]
    setRouteQuery(
      combineCurrentQuery([
        ...(key
          ? [
              {
                key,
                values: condition.value as string,
                action: Action.Add,
              },
            ]
          : []),
        pageQueryAction.value,
      ])
    )

    await reloadProducts()
  }

  const unselectFilter = async (condition: ProductCondition) => {
    removeActiveFilters(condition)

    const key = facetLabels.value?.[condition.field]
    setRouteQuery(
      combineCurrentQuery([
        ...(key
          ? [
              {
                key,
                values: condition.value as string,
                action: Action.Remove,
              },
            ]
          : []),
        pageQueryAction.value,
      ])
    )

    await reloadProducts()
  }

  const clearAllFilters = async () => {
    clearActiveFilters()
    resetCurrentPriceRange()

    // need to keey limit query, reset query to improve performance
    setRouteQuery(
      combineQuery(
        {},
        {
          key: FilterFields.Limit,
          values: limit.value.toString(),
          action: limit.value === DEFAULT_LIMIT ? Action.Empty : Action.Replace,
        }
      )
    )
    await reloadProducts()
  }

  const filterProductsByPrice = async (range: Range) => {
    currentPriceRange.value = range

    const key = facetLabels.value?.[FilterFields.Price]
    setRouteQuery(
      combineCurrentQuery([
        ...(key
          ? [
              {
                key,
                values: `${range.min}_${range.max}`,
                action: Action.Replace,
              },
            ]
          : []),
        pageQueryAction.value,
      ])
    )

    await reloadProducts()
  }
  console.log('useProductList watch')
  watch(
    () => route.value.query,
    () => {
      if (loading.value.value) return
      loadProducts(true)
    }
  )

  return {
    products: readonly(products),
    productLoadedFromServer: readonly(productLoadedFromServer),
    options: readonly(options),
    sortOptions: computed(() => options.value?.sortOptions),
    limitOptions: computed(() => options.value?.limitOptions),
    activeSort: readonly(activeSort),
    loading: readonly(loading.value),
    total: readonly(total),
    offset: readonly(offset),
    limit: readonly(limit),
    filters: allowedFilters,
    activeFilters: readonly(activeFilters),
    activeFilterGroups,
    currentPriceRange: readonly(currentPriceRange),
    priceRange: readonly(priceRange),
    hasResult,

    ensureOptions,
    loadProducts,
    refreshStocks,
    sortProducts,
    limitProducts,
    offsetProducts,
    selectFilter,
    unselectFilter,
    removeActiveFilterGroup,
    clearAllFilters,
    filterProductsByPrice,
  }
}

// provide productList
export const productListProviderKey: InjectionKey<
  ReturnType<typeof useProductList>
> = Symbol('Provider:ProductList')

export const provideProductList = () => {
  const result = useProductList()

  provide(productListProviderKey, result)
}

// inject productList
export const injectProductList = () => {
  const result = inject(productListProviderKey)
  if (!result) {
    throw new Error('product list provider not set')
  }

  return result
}
